<template>
  <div
    class="cart-item"
    :class="{ processing: data.cartStatus === 0 }"
  >
    <div
      @click="handleCheck(data)"
      class="check"
    >
      <img
        v-if="isUnavailable(data.cartStatus)"
        src="@/assets/H5/carts/disabled-check.svg"
        alt=""
      >
      <template v-else>
        <img
          v-if="data.checked"
          src="@/assets/order/cart-checked.svg"
          alt=""
        >
        <img
          v-else
          src="@/assets/images/circle-uncheck.svg"
          alt=""
        >
      </template>
    </div>
    <div class="item-info">
      <Image
        @click="goDetailPage(data)"
        :style="{ opacity: isUnavailable(data.cartStatus) ? '.4': 1 }"
        class="sku-image"
        :src="data.skuImage"
        alt=""
      />
      <div class="sku-desc">
        <div class="top">
          <div class="sku-price">
            <div
              class="price-info"
              :style="{ color: isUnavailable(data.cartStatus) ? '#b2b7c2' : '#232323' }"
            >
              <span>{{ currencyData.targetCurrency.slice(0, 2) }}&nbsp;</span>
              <span>{{ currencyData.targetUnit }}</span>
              <span>{{ bankersRound(currencyData?.rate * data?.skuPriceRmb, 2) }}</span>
            </div>
            <div
              v-if="data.cartStatus === 0"
              @click="$emit('showProcessDialog')"
              class="processing-status"
            >
              Processing
              <SvgIcon
                :svg="rightArrowSvg"
                color="#DB7E25"
                width="10px"
                height="10px"
              />
            </div>
          </div>
          <div
            class="sku-info"
          >
            <div
              class="text"
              :style="{ color: isUnavailable(data.cartStatus) ? '#b2b7c2' : '#5c5c5c' }"
              @click="goDetailPage(data)"
            >
              {{ data.spuName }}
            </div>
            <div
              @click="handleDelete(data)"
              @mouseenter="isDeleteHover = true"
              @mouseleave="isDeleteHover = false"
              :class="{ offset: data.cartStatus === 0 }"
              class="delete-icon"
            >
              <img
                v-if="isDeleteHover"
                src="@/assets/order/delete-icon-hover.svg"
                width="20px"
                alt=""
              >
              <img
                v-else
                src="@/assets/images/delete-icon.svg"
                width="20px"
                alt=""
              >
            </div>
          </div>
          <div class="sku-attr">
            <img
              :src="data.skuImage"
              width="16px"
              alt=""
              style="border-radius: 1px;"
            >
            <div
              class="attr"
              :style="{ color: isUnavailable(data.cartStatus) ? '#b2b7c2' : '#5c5c5c' }"
            >
              {{ formatAttr(data.skuSpec) }}
            </div>
          </div>
        </div>
        <div class="bottom">
          <div class="remark">
            <template v-if="(getSpuBatchNum(data.spuId) > getSpuQuantity(data.spuId) && getSpuQuantity(data.spuId) > 0) && !data.unavailableMsg">
              The minimum order quantity of
              {{ getSpuBatchNum(data.spuId) }} items has not been met.
            </template>
            <div
              v-if="data.unavailableMsg"
              class="unavailableMsg"
            >
              {{ data.unavailableMsg }} <span
                @click="skipThirdPage"
                class="contact"
              >Contact us</span>
            </div>
          </div>
          <div class="bottm-right">
            <NumberInput
              class="count-option"
              :min="1"
              :disabled-minus="getSpuBatchNum(data.spuId) >= getSpuQuantity(data.spuId) || isUnavailable(data.cartStatus) || data.skuQuantity <= data.bundleQuantity"
              :disabled-plus="isUnavailable(data.cartStatus)"
              @change="handleManual(data)"
              @minus="handleMinus(data)"
              @plus="handlePlus(data)"
              v-model="data.skuQuantity"
            />
            <div class="total-price">
              <div
                class="subtotal"
                :style="{ color: isUnavailable(data.cartStatus) ? '#b2b7c2' : '#5c5c5c' }"
              >
                Subtotal
              </div>
              <div class="price">
                <IntlCurrency
                  :count="(bankersRound(currencyData?.rate * data.skuPriceRmb, 2) * data.skuQuantity).toFixed(2)"
                  :size="14"
                  :color="subTotalColor(data.cartStatus)"
                  :currency="currencyData?.targetCurrency.slice(0, 2)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { bankersRound, debounce } from '@/utils/utils'
import { fetchData } from '@/utils/fetch'
import { useRouter } from 'vue-router'
import { usePlugin } from '@/hooks/usePlugin'
import rightArrowSvg from '@/assets/offer/right-arrow.svg?raw'
import { useStore } from 'vuex'
import { useIntercom } from '@/hooks/useIntercom'
import { Image } from '@/components/Image'

const props = defineProps({
  data: {
    type: Object,
    default: () => {}
  },
  currencyData: {
    type: Object,
    default: () => {}
  },
  list: {
    type: Array,
    default: () => []
  }
})

const emits = defineEmits(['fetchList', 'showProcessDialog', 'showExceedQuantityDialog'])

const router = useRouter()

const store = useStore()

const { $toast, $confirm } = usePlugin()
const intercom = useIntercom()

const isDeleteHover = ref(false)

const minOrderQuantity = ref(0)

const handleCheck = (item) => {
  if (isUnavailable(item.cartStatus)) return

  const list = props.list
  if (list.filter((item) => item.checked).length >= 50 && !item.checked) {
    $toast('You can only select up to 50 items', { type: 'error' })
    return
  }

  const spuBatchNum = getSpuBatchNum(item.spuId)

  // 如果是取消勾选
  if (item.checked) {
    // 获取同一SPU下其他已勾选的SKU数量总和
    const otherCheckedQuantity = list.filter(v => v.spuId === item.spuId && v.checked && v.cartId !== item.cartId).reduce((sum, v) => sum + v.skuQuantity, 0)

    // 如果还有其他已勾选的SKU,检查数量是否满足最小起批量
    if (otherCheckedQuantity > 0 && otherCheckedQuantity < spuBatchNum) {
      $toast(
        `The minimum order quantity of ${spuBatchNum} items has not been met.`,
        { type: 'error' }
      )
      return
    }
  }

  item.checked = !item.checked
  updateProductSkuPrice(item)
}

const updateCart = async (cartId, obj) => {
  const { data, code } = await fetchData('/appb/b2b/cart/cartUpdate', {
    method: 'POST',
    body: JSON.stringify({
      cartId,
      quantity: obj.skuQuantity
    })
  })
  if (code === 200) {
    obj.skuQuantity = data
    store.dispatch('cart/updateCartCount')
  } else if (code === 100001) {
    emits('showExceedQuantityDialog', minOrderQuantity.value)
  } else if (code === 200001) {
    $toast('Please fill delivery address.')
    localStorage.removeItem('addressForm')
    showAddressInfoDialog.value = true
  }
}

// 捆绑数量算法
const calcBundleQuantity = (data) => {
  const bundleQuantity = +data.bundleQuantity
  if (bundleQuantity === 1) return

  const halfBundle = bundleQuantity / 2
  let quantity = data.skuQuantity
  quantity = (Math.floor((quantity + halfBundle) / bundleQuantity)) * bundleQuantity
  if (quantity <= 0) {
    quantity = bundleQuantity
  }
  data.skuQuantity = quantity
}

const handleMinus = (data) => {
  data.skuQuantity++
  data.skuQuantity -= data.bundleQuantity
  if (data.skuQuantity <= 0) {
    data.skuQuantity = 1
  }
  updateProductSkuPrice(data)
  minOrderQuantity.value = data.batchNum
  updateCart(data.cartId, data)
}

const handlePlus = (data) => {
  data.skuQuantity--
  data.skuQuantity += data.bundleQuantity
  updateProductSkuPrice(data)
  minOrderQuantity.value = data.batchNum
  updateCart(data.cartId, data)
}

const handleManual = debounce((data) => {
  calcBundleQuantity(data)
  minOrderQuantity.value = data.batchNum
  if (Number(data.skuQuantity) > 0) {
    updateCart(data.cartId, data)
  } else {
    data.skuQuantity = 1
    updateCart(data.cartId, data)
  }
  updateProductSkuPrice(data)
}, 300)

const goDetailPage = (data) => {
  const { href } = router.resolve({
    path: `/offer/${data.spuId}`
  })

  window.open(href, '_blank')
}

const handleDelete = (data) => {
  $confirm('Delete item?', 'Are you sure you want to delete itme from cart?').then(async () => {
    const { code } = await fetchData('/appb/b2b/cart/cartDelete', {
      method: 'post',
      body: JSON.stringify({
        cartIds: [data.cartId]
      })
    })
    if (code === 200) {
      $toast('Deleted successfully.', { type: 'success' })
      emits('fetchList')
    }
  })
}

const getSpuBatchNum = (spuId) => {
  const spuItems = props.list.filter((item) => item.spuId === spuId)
  return Math.max(...spuItems.map((item) => item.batchNum))
}

const getSpuQuantity = (spuId) => {
  const spuItems = props.list.filter((item) => item.spuId === spuId)
  return spuItems.reduce((sum, item) => sum + Number(item.skuQuantity), 0)
}

// 获取起批量对应价格
const getPriceByQuantity = (quantity, priceRange) => {
  let validPrices = priceRange.filter(item => item.startQuantity <= quantity)
  if (validPrices.length === 0) {
    validPrices = [priceRange[0]]
  }

  const bestMatch = validPrices.reduce((prev, curr) =>
    curr.startQuantity > prev.startQuantity ? curr : prev
  )

  return Number(bestMatch.price)
}

// 更新同级商品下sku价格
const updateProductSkuPrice = (obj) => {
  // 先更新自身
  obj.skuPriceRmb = getPriceByQuantity(obj.skuQuantity, obj.skuPriceCnyRanges)

  // 单个区间 return
  const skuPriceCnyRanges = obj.skuPriceCnyRanges || []
  if (skuPriceCnyRanges.length < 2) return

  // 获取已选中的同个商品级别下的sku加购总数
  let count = 0
  const checkedList = props.list.filter(v => v.checked)
  const sameProductLevelSku = checkedList.filter(v => v.spuId === obj.spuId)
  sameProductLevelSku.forEach(item => {
    count += item.skuQuantity
  })

  if (count) {
    const skuPriceRmb = getPriceByQuantity(count, obj.skuPriceCnyRanges)
    // 同级sku同步更新金额
    sameProductLevelSku.forEach(item => item.skuPriceRmb = skuPriceRmb)
  }
}

const formatAttr = (res) => {
  if (res) {
    const _li = res.split(';').map(item => item.split(':')[1])
    return _li.join('; ')
  }
}

const isUnavailable = (status) => {
  return [50, 51, 52].includes(Number(status))
}

const subTotalColor = (status) => {
  if (status === 0) return '#db7e25'
  if (isUnavailable(status)) return '#6E717A'
  return '#052785'
}

const skipThirdPage = async () => {
  intercom.showNewMessage()
}

</script>

<style lang='less' scoped>
.cart-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 16px 0;
  padding: 16px 0;

  &:last-child {
    margin-bottom: 0;
  }
  &::after {
    content: '';
    position: absolute;
    right: 0;
    bottom: 0;
    width: calc(690px - 16px);
    height: 1px;
    background-color: var(--light-split-color);
  }
  &.processing {
    background-color: #FFFCF5;
  }
  .check {
    width: 20px;
    height: 20px;
    cursor: pointer;
    margin-left: 8px;
    img {
      width: 100%;
      height: 100%;
    }
  }

  .item-info {
    flex: 1;
    display: flex;
    gap: 16px;

    .sku-image {
      width: 120px;
      height: 120px;
      border-radius: 4px;
      cursor: pointer;
    }

    .sku-desc {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      height: 120px;
      .sku-price {
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: 20px;

        .price-info {
          display: flex;
          align-items: center;
          color: #232323;
          font-size: 12px;
          font-family: mon-600;
        }

        .processing-status {
          color: #db7e52;
          font-size: 11px;
          font-family: mon-600;
          cursor: pointer;
          &:hover {
            text-decoration: underline;
          }
        }
      }
      .sku-info {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .text {
          max-width: 622px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: #5c5c5c;
          font-family: mon-500;
          font-size: 12px;
          cursor: pointer;
        }
        .delete-icon {
          width: 20px;
          height: 20px;
          cursor: pointer;

          &.offset {
            position: relative;
            top: 4px;
          }
        }
      }
      .sku-attr {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-top: 8px;
        width: fit-content;
        min-width: 200px;
        max-width: 400px;
        height: 24px;
        padding: 4px;
        padding-right: 8px;
        background-color: #f8f8f8;
        border-radius: 2px;
        .attr {
          color: #5c5c5c;
          font-size: 12px;
          font-family: mon-500;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
      .top {
        padding-right: 8px;
      }
      .bottom {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        gap: 24px;
        padding-right: 8px;
        .remark {
          display: flex;
          align-items: flex-end;
          color: #f53131;
          font-size: 12px;
          font-family: mon-500;
          min-width: 200px;

          .unavailableMsg {
            max-width: 400px;
            .contact {
              color: #232323;
              font-size: 12px;
              font-family: mon-500;
              text-decoration: underline;
              &:hover {
                color: var(--link-hover-color);
                cursor: pointer;
              }
            }
          }
        }

        .bottm-right {
          display: flex;
          gap: 48px;
        }

        .count-option {
          width: 100px;
          height: 28px;
          margin-top: 8px;
        }

        .total-price {
          display: flex;
          flex-direction: column;
          gap: 2px;
          text-align: right;
          min-width: 90px;
          .subtotal {
            color: #5c5c5c;
            font-size: 12px;
            font-family: mon-500;
          }
          .price {
            color: #052784;
            font-size: 16px;
            font-family: mon-700;
            height: 20px;
          }
        }
      }
    }
  }
}
</style>
